import { useMemo, useState } from "react"
import { toast } from "sonner"
import { Award, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { UnassignDialog } from "@/components/manager/UnassignDialog"
import {
  Pill,
  RowPill,
  awaitsReview,
  formatDay,
} from "@/components/manager/RoundGroup"
import { useAuth } from "@/contexts/AuthContext"
import {
  useManagerAssignmentAttempts,
  useManagerAssignments,
  useManagerRounds,
  useResetAttempts,
  useReviewAssignment,
} from "@/lib/queries"
import type { ManagerAssignmentRow } from "@/lib/types"

const OUTCOMES = {
  completed: { label: "Completed", tone: "green" },
  not_completed: { label: "Not completed", tone: "muted" },
  removed: { label: "Removed", tone: "muted" },
} as const

function OutcomePill({ row }: { row: ManagerAssignmentRow }) {
  if (!row.closed_at || !row.closed_reason) return <Pill label="Open" tone="blue" />
  return <Pill {...OUTCOMES[row.closed_reason]} />
}

function AttemptList({ assignmentId }: { assignmentId: number }) {
  const { data: attempts, isLoading } = useManagerAssignmentAttempts(assignmentId)
  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading attempts…</p>
  }
  if (!attempts || attempts.length === 0) {
    return <p className="text-xs text-muted-foreground">No attempts yet</p>
  }
  return (
    <ul className="space-y-1 text-xs">
      {attempts.map((attempt) => (
        <li key={attempt.id} className="flex flex-wrap gap-x-2 text-muted-foreground">
          <span className="font-medium text-foreground">Attempt {attempt.number}</span>
          {attempt.status === "in_progress" ? (
            <span>In progress</span>
          ) : (
            <>
              <span>{formatDay(attempt.submitted_at)}</span>
              {attempt.score !== null && <span>{attempt.score}%</span>}
              <span>{attempt.passed ? "Passed" : "Not passed"}</span>
            </>
          )}
          <span>Version {attempt.version_label}</span>
        </li>
      ))}
    </ul>
  )
}

function RoundCard({
  row,
  source,
  onRemove,
  onViewCertificate,
}: {
  row: ManagerAssignmentRow
  source: string
  onRemove: (row: ManagerAssignmentRow) => void
  onViewCertificate: (assignmentId: number) => void
}) {
  const { user } = useAuth()
  const reviewAssignment = useReviewAssignment()
  const resetAttempts = useResetAttempts()
  const open = row.closed_at === null

  const handleReview = async () => {
    try {
      await reviewAssignment.mutateAsync(row.id)
      toast.success("Marked as reviewed")
    } catch {
      toast.error("Failed to mark as reviewed")
    }
  }

  const handleReset = async () => {
    try {
      await resetAttempts.mutateAsync(row.id)
      toast.success("Attempts reset")
    } catch {
      toast.error("Failed to reset attempts")
    }
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{source}</p>
          <p className="text-xs text-muted-foreground">
            Assigned {formatDay(row.assigned_on)} ·{" "}
            {row.due_on ? `Due ${formatDay(row.due_on)}` : "No due date"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <OutcomePill row={row} />
          {/* Status for an open round; on a closed one, only the review marker. */}
          {(open || (row.closed_reason !== "removed" && awaitsReview(row))) && (
            <RowPill row={row} />
          )}
        </div>
      </div>

      <AttemptList assignmentId={row.id} />

      <div className="flex flex-wrap items-center gap-2">
        {row.manager_reviewed_at ? (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">
            Reviewed {formatDay(row.manager_reviewed_at)}
          </span>
        ) : (
          // Never offered on one's own round: the same person cannot take an
          // assessment and attest to its managerial review.
          awaitsReview(row) &&
          row.user_id !== user?.id && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleReview}
              disabled={reviewAssignment.isPending}
            >
              Mark reviewed
            </Button>
          )
        )}
        {row.status === "passed" && (
          <Button size="sm" variant="outline" onClick={() => onViewCertificate(row.id)}>
            <Award className="mr-1.5 h-4 w-4" />
            Certificate
          </Button>
        )}
        {open && row.locked && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleReset}
            disabled={resetAttempts.isPending}
          >
            Reset attempts
          </Button>
        )}
        {open && (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => onRemove(row)}
          >
            Remove this round
          </Button>
        )}
      </div>
    </div>
  )
}

// Every round one person has had of one assessment, newest first, including
// removed ones, with the actions that apply to each.
export function PersonRoundsSheet({
  quizId,
  userId,
  onOpenChange,
  onViewCertificate,
}: {
  quizId: number | null
  userId: number | null
  onOpenChange: (open: boolean) => void
  onViewCertificate: (assignmentId: number) => void
}) {
  const { data: assignments = [], isLoading } = useManagerAssignments()
  const { data: rounds = [] } = useManagerRounds()
  const [removing, setRemoving] = useState<ManagerAssignmentRow | null>(null)

  const rows = useMemo(
    () =>
      assignments
        .filter((a) => a.quiz_id === quizId && a.user_id === userId)
        .sort(
          (a, b) => b.assigned_on.localeCompare(a.assigned_on) || b.id - a.id
        ),
    [ assignments, quizId, userId ]
  )
  const groupNames = useMemo(
    () => new Map(rounds.map((round) => [ round.id, round.group_name ])),
    [ rounds ]
  )

  const first = rows[0]
  const open = quizId !== null && userId !== null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{first?.staff_name ?? "Rounds"}</SheetTitle>
          <SheetDescription>{first?.quiz_title}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No rounds found.</p>
          ) : (
            rows.map((row) => (
              <RoundCard
                key={row.id}
                row={row}
                source={
                  row.quiz_round_id === null
                    ? "One-off"
                    : (groupNames.get(row.quiz_round_id) ?? "Round")
                }
                onRemove={setRemoving}
                onViewCertificate={onViewCertificate}
              />
            ))
          )}
        </div>
        <UnassignDialog
          assignment={removing}
          onOpenChange={(isOpen) => !isOpen && setRemoving(null)}
        />
      </SheetContent>
    </Sheet>
  )
}
